import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { RegistrationStammdatenPlugin } from '@adornis/buchungsheld-db/db-plugins.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { IcsrmStammdaten } from './icsrm-stammdaten-plugin.js';

const countWords = (text: string | null | undefined): number =>
  (text ?? '').trim().split(/\s+/).filter(Boolean).length;

const wordCount =
  (opts: { min: number; max: number }) =>
  ({ value, key }: { value: unknown; key: string }) => {
    const count = countWords(typeof value === 'string' ? value : '');
    if (count < opts.min) {
      throw new ValidationError(`Mindestens ${opts.min} Wörter erforderlich (aktuell: ${count})`, {
        key: key as any,
        translationKey: 'validation_word_count_too_few',
      });
    }
    if (count > opts.max) {
      throw new ValidationError(`Maximal ${opts.max} Wörter erlaubt (aktuell: ${count})`, {
        key: key as any,
        translationKey: 'validation_word_count_too_many',
      });
    }
  };

const mustBeTrue =
  () =>
  ({ value, key }: { value: unknown; key: string }) => {
    if (value !== true) {
      throw new ValidationError('Zustimmung erforderlich', {
        key: key as any,
        translationKey: 'validation_accept_required',
      });
    }
  };

export const deutschkursRegistrationStammdatenPlugin: RegistrationStammdatenPlugin = {
  id: 'registration::stammdaten::deutschkurs',
  type: 'registration::stammdaten',
  disableCustomFormfields: true,
  createStammdaten: async ({ userID, institutionID, createInDB } = {}) => {
    const stammdaten = new DeutschkursStammdaten({ userID, institutionID });
    if (createInDB) await stammdaten.create();
    return stammdaten;
  },
  getFields: () => {
    return Object.keys(DeutschkursStammdaten.fields).filter(
      it => !['_id', '_class', 'userID', 'institutionID'].includes(it),
    );
  },
  classType: () => DeutschkursStammdaten,
};

@Entity()
export class DeutschkursStammdaten extends IcsrmStammdaten {
  static override _class = 'DeutschkursStammdaten';

  override getTranslationNamespace() {
    return 'deutschkurs';
  }

  @validate(wordCount({ min: 100, max: 200 }))
  @Field(type => String)
  motivationText: Maybe<string>;

  @validate(wordCount({ min: 50, max: 150 }))
  @Field(type => String)
  scheduleText: Maybe<string>;

  @validate(nonOptional())
  @Field(type => String)
  b1CertFileID: Maybe<string>;

  @validate(nonOptional())
  @Field(type => String)
  immaCertFileID: Maybe<string>;

  @validate(mustBeTrue())
  @Field(type => Boolean, { default: v => v ?? false })
  acceptedPlacementTest: Maybe<boolean>;

  @validate(mustBeTrue())
  @Field(type => Boolean, { default: v => v ?? false })
  acceptedWeeklyTimeCommitment: Maybe<boolean>;

  @validate(mustBeTrue())
  @Field(type => Boolean, { default: v => v ?? false })
  acceptedAttendancePolicy: Maybe<boolean>;
}
